import { useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren
} from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'

import {
  authorizationTokenCookieRetrieve,
  authorizationTokenLogoutRetrieve,
  usersLoginAdminCreate,
  type CurrentAccountUser
} from 'src/api/main'
import { LOGIN_IN_PROGRESS_KEY, LOGOUT_ENFORCED_KEY } from 'src/constants'
import { allLangs } from 'src/locales'
import { authLoginPaths, isAuthLoginPath, paths } from 'src/router'
import {
  clearLocalStorage,
  exchangeRefreshForAccess,
  getLocalStorageTokens,
  setLocalStorageToken,
  useShowApiErrors
} from 'src/utils'

import { AppLoadingScreen } from '../components'

import {
  shouldDropStoredSession,
  shouldLeaveLoginPage,
  shouldProbeCookieSession,
  shouldResolveStoredSession
} from './sessionGate'
import { UserSessionContext } from './UserSessionContext'

// These query helpers were dropped from the generated client (POST-as-query pattern).
// Defined locally to preserve the existing cache-key contract.
const usersLoginAdminCreateQueryKey = () => ['usersLoginAdminCreate'] as const
const usersLoginAdminCreateOptions = () => ({
  queryKey: usersLoginAdminCreateQueryKey(),
  queryFn: async () => {
    const { data } = await usersLoginAdminCreate({ throwOnError: true })

    return data as CurrentAccountUser
  }
})

const PUBLIC_AUTH_PAGES = [
  ...authLoginPaths,
  paths.auth.register,
  paths.auth.confirmRegister,
  paths.auth.resetPassword,
  paths.auth.resetPasswordConfirm,
  paths.auth.organizations,
  paths.auth.invitation,
  paths.auth.otp,
  paths.auth.emailOtpVerify,
  paths.auth.emailOtpLink,
  paths.auth.confirmMfaOtp,
  paths.auth.confirmMfaBackupCode,
  paths.auth.onboarding,
  paths.auth.googleConnectCallback,
  paths.auth.googleClientCallback,
  paths.auth.googleClientConnectCallback,
  paths.auth.entraIdConnectCallback,
  paths.auth.entraIdOrgConnectCallback,
  paths.auth.appleClientCallback,
  paths.auth.appleClientConnectCallback,
  paths.auth.appleOrgConnectCallback,
  paths.termsAndConditions
]

type CookieResult = { ok: true } | { ok: false }
const ACTIVE_WORKSPACE_STORAGE_KEY = 'active-workspace-id'

const getWorkspaceIdFromPath = (pathname: string): number | undefined => {
  const match = pathname.match(/^\/workspaces\/([^/]+)/)
  const rawId = match?.[1]

  if (!rawId || rawId === 'new') {
    return undefined
  }

  const workspaceId = Number(rawId)

  return Number.isFinite(workspaceId) ? workspaceId : undefined
}

const getStoredWorkspaceId = () => {
  if (typeof window === 'undefined') {
    return undefined
  }

  const rawValue = window.localStorage.getItem(ACTIVE_WORKSPACE_STORAGE_KEY)
  if (!rawValue) {
    return undefined
  }

  const workspaceId = Number(rawValue)

  return Number.isFinite(workspaceId) ? workspaceId : undefined
}

export const UserSessionContextProvider = ({ children }: PropsWithChildren) => {
  const showApiErrors = useShowApiErrors()
  const { i18n } = useTranslation()
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const queryClient = useQueryClient()
  const hasRedirectedOnSuccessRef = useRef(false)
  const hasRedirectedOnFailureRef = useRef(false)
  const [activeWorkspaceId, setActiveWorkspaceIdState] = useState<number>()
  // Bumped when a dead token is dropped: `hasAccessToken` is read from storage
  // on every render, so one render is all it takes for the rest of the provider
  // to see the browser as signed out.
  const [, setStoredSessionDropped] = useState(false)

  const normalizedPathname =
    pathname.length > 1 && pathname.endsWith('/')
      ? pathname.slice(0, -1)
      : pathname
  const isOnPublicAuthPage = PUBLIC_AUTH_PAGES.some(
    (path) => normalizedPathname === path || normalizedPathname.endsWith(path)
  )
  const isOnLoginPage = isAuthLoginPath(normalizedPathname)
  const isBrowser = typeof window !== 'undefined'

  // A sign-in attempt belongs to the page instance that started it: the flag is
  // only there so this provider does not hijack navigation while that attempt
  // is still running. Let it survive a page load and it becomes a trap — the
  // login page suppresses its own "you are already signed in" redirect for the
  // rest of the tab's life. A fresh mount has no attempt in flight.
  const hasClearedStaleLoginFlagRef = useRef(false)
  if (isBrowser && !hasClearedStaleLoginFlagRef.current) {
    hasClearedStaleLoginFlagRef.current = true
    sessionStorage.removeItem(LOGIN_IN_PROGRESS_KEY)
  }

  const isLoginInProgress =
    isBrowser && sessionStorage.getItem(LOGIN_IN_PROGRESS_KEY) === '1'
  const isLogoutEnforced =
    isBrowser && sessionStorage.getItem(LOGOUT_ENFORCED_KEY) === '1'
  const hasAccessToken = isBrowser && Boolean(getLocalStorageTokens().access)

  const resetSessionState = useCallback(
    (enforceLogout: boolean) => {
      clearLocalStorage()
      queryClient.clear()
      hasRedirectedOnSuccessRef.current = false
      hasRedirectedOnFailureRef.current = false
      if (isBrowser) {
        window.localStorage.removeItem(ACTIVE_WORKSPACE_STORAGE_KEY)
        sessionStorage.removeItem(LOGIN_IN_PROGRESS_KEY)
        if (enforceLogout) {
          sessionStorage.setItem(LOGOUT_ENFORCED_KEY, '1')
        } else {
          sessionStorage.removeItem(LOGOUT_ENFORCED_KEY)
        }
      }
    },
    [isBrowser, queryClient]
  )

  const {
    data: cookieData,
    isLoading: isCookieLoading,
    isError: isCookieError,
    error: cookieError,
    refetch: refetchCookie
  } = useQuery<CookieResult>({
    queryKey: ['session', 'cookie'],
    queryFn: async () => {
      const { data } = await authorizationTokenCookieRetrieve({
        throwOnError: true
      })
      if (Array.isArray(data)) {
        const refreshFromList = data[0]?.refresh
        if (!refreshFromList) return { ok: false } as CookieResult
        const tokens = await exchangeRefreshForAccess(refreshFromList)
        setLocalStorageToken(tokens)
        return { ok: true } as CookieResult
      }

      if (data && typeof data === 'object') {
        const maybeTokens = data as { access?: string; refresh?: string }
        if (maybeTokens.access && maybeTokens.refresh) {
          setLocalStorageToken({
            access: maybeTokens.access,
            refresh: maybeTokens.refresh
          })
          return { ok: true } as CookieResult
        }
        if (maybeTokens.refresh) {
          const tokens = await exchangeRefreshForAccess(maybeTokens.refresh)
          setLocalStorageToken(tokens)
          return { ok: true } as CookieResult
        }
      }

      return { ok: false } as CookieResult
    },
    retry: false,
    enabled: shouldProbeCookieSession({ hasAccessToken, isLogoutEnforced }),
    staleTime: Infinity,
    gcTime: Infinity
  })

  const cookieStatus = (cookieError as AxiosError | undefined)?.response?.status
  const isCookieUnauthorized =
    isCookieError &&
    cookieStatus != null &&
    cookieStatus >= 400 &&
    cookieStatus < 500
  const isCookieNoSession =
    !hasAccessToken &&
    !isCookieLoading &&
    !isCookieError &&
    (cookieData === undefined || !cookieData.ok)

  const {
    data: user,
    isLoading: isUserLoading,
    error: userError,
    refetch: refetchUser
  } = useQuery({
    ...usersLoginAdminCreateOptions(),
    enabled: shouldResolveStoredSession({
      isOnPublicAuthPage,
      isOnLoginPage,
      hasAccessToken,
      isCookieSessionOk: !!cookieData?.ok
    }),
    staleTime: Infinity,
    refetchOnMount: false
  })

  const userErrorStatus = (userError as AxiosError | undefined)?.response
    ?.status
  const isProfileProbeRejected =
    userErrorStatus === 401 || userErrorStatus === 403

  const shouldRedirectToLogin =
    !isOnPublicAuthPage &&
    !isLogoutEnforced &&
    !hasAccessToken &&
    (isCookieUnauthorized || isCookieNoSession)

  useEffect(() => {
    if (
      isCookieUnauthorized &&
      !hasAccessToken &&
      !hasRedirectedOnFailureRef.current
    ) {
      hasRedirectedOnFailureRef.current = true
    }
  }, [isCookieUnauthorized, hasAccessToken])

  useEffect(() => {
    if (!shouldDropStoredSession({ hasAccessToken, isProfileProbeRejected })) {
      return
    }

    // Dropping it re-arms the cookie handoff, which then either recovers a real
    // session or sends the browser to the login form — both better than holding
    // a token nothing will honour.
    clearLocalStorage()
    setStoredSessionDropped(true)
  }, [hasAccessToken, isProfileProbeRejected])

  useEffect(() => {
    if (
      shouldLeaveLoginPage({
        isOnLoginPage,
        hasResolvedUser: !!user,
        hasAccessToken,
        isCookieSessionOk: !!cookieData?.ok,
        isLogoutEnforced,
        isLoginInProgress
      }) &&
      !hasRedirectedOnSuccessRef.current
    ) {
      hasRedirectedOnSuccessRef.current = true
      const params = new URLSearchParams(search)
      const redirectUrl = params.get('redirect')
      let targetUrl = paths.home
      if (redirectUrl) {
        try {
          targetUrl = decodeURIComponent(redirectUrl)
        } catch {
          targetUrl = paths.home
        }
      }
      navigate(targetUrl, { replace: true })
    }
  }, [
    cookieData,
    user,
    hasAccessToken,
    isOnLoginPage,
    search,
    navigate,
    isLogoutEnforced,
    isLoginInProgress
  ])

  const removeUserData = useCallback(
    (options?: { redirectTo?: string }) => {
      void (async () => {
        if (isBrowser) {
          sessionStorage.setItem(LOGOUT_ENFORCED_KEY, '1')
          sessionStorage.removeItem(LOGIN_IN_PROGRESS_KEY)
        }
        const { access } = getLocalStorageTokens()
        try {
          await authorizationTokenLogoutRetrieve({
            throwOnError: true,
            ...(access && {
              headers: {
                Authorization: `Token ${access}`
              }
            })
          })
        } catch (error) {
          showApiErrors(error)
        } finally {
          resetSessionState(true)
          const target = options?.redirectTo
            ? `${paths.auth.login}?redirect=${encodeURIComponent(options.redirectTo)}`
            : paths.auth.login
          navigate(target, { replace: true })
        }
      })()
    },
    [isBrowser, navigate, resetSessionState, showApiErrors]
  )

  const refreshSession = useCallback(async () => {
    hasRedirectedOnFailureRef.current = false
    return refetchCookie()
  }, [refetchCookie])

  const updateUserData = useCallback(
    (data?: CurrentAccountUser) => {
      if (data !== undefined) {
        // Signing in swaps the principal, so everything cached before it was
        // fetched as somebody else — or as nobody. Chief among them the
        // workspace permission list, which 401s pre-auth and then sticks as
        // "you don't have access" until a reload. Clearing first also drops the
        // thin sign-in payload's competitors so the seed below wins.
        queryClient.clear()
        // The sign-in response is a usable session immediately, but a thinner
        // one: aggregates such as `organizations[].users` come back null there.
        // Seed it so the app can render at once, then ask the session endpoint
        // for the full record — `refetchOnMount: false` means nothing else will.
        queryClient.setQueryData(usersLoginAdminCreateQueryKey(), data)
        void refetchUser()
      } else {
        void refetchUser()
      }
    },
    [queryClient, refetchUser]
  )

  const setActiveWorkspaceId = setActiveWorkspaceIdState

  const workspaceIdFromPath = useMemo(
    () => getWorkspaceIdFromPath(pathname),
    [pathname]
  )

  useEffect(() => {
    // Wait for the session to load before deriving or clearing anything. While
    // `user` is undefined, `organizations` reads as empty — indistinguishable
    // from a user who genuinely has none. Acting on that would wipe the
    // remembered workspace on every reload, so a returning user would be sent
    // to the selector instead of straight into their workspace.
    if (!user) {
      return
    }

    const availableWorkspaceIds = (user.organizations ?? [])
      .map((organization) => Number(organization.id))
      .filter((workspaceId) => Number.isFinite(workspaceId))

    if (availableWorkspaceIds.length === 0) {
      setActiveWorkspaceIdState(undefined)
      if (isBrowser) {
        window.localStorage.removeItem(ACTIVE_WORKSPACE_STORAGE_KEY)
      }

      return
    }

    const storedWorkspaceId = isBrowser ? getStoredWorkspaceId() : undefined

    const nextWorkspaceId = [
      workspaceIdFromPath,
      activeWorkspaceId,
      storedWorkspaceId,
      availableWorkspaceIds[0]
    ].find(
      (workspaceId): workspaceId is number =>
        workspaceId != null && availableWorkspaceIds.includes(workspaceId)
    )

    if (nextWorkspaceId !== activeWorkspaceId) {
      setActiveWorkspaceIdState(nextWorkspaceId)
    }
  }, [activeWorkspaceId, isBrowser, workspaceIdFromPath, user])

  // Persist the workspace the user is *intentionally* in (a valid `/workspaces/:id/*`
  // route), so it can be offered as their remembered choice on the next login.
  // The post-login `/` fallback to the first workspace is deliberately NOT
  // persisted — otherwise every first login would look like a remembered choice
  // and the selector would never appear. Removing a stale value is owned by the
  // empty-orgs branch above and logout (`resetSessionState`).
  useEffect(() => {
    if (!isBrowser || workspaceIdFromPath == null) {
      return
    }

    const isAvailable = (user?.organizations ?? []).some(
      (organization) => Number(organization.id) === workspaceIdFromPath
    )
    if (!isAvailable) {
      return
    }

    window.localStorage.setItem(
      ACTIVE_WORKSPACE_STORAGE_KEY,
      workspaceIdFromPath.toString()
    )
  }, [workspaceIdFromPath, isBrowser, user?.organizations])

  // Resolve the active UI language once the session is known (TS-CORE-027).
  // Precedence: the user's saved language (if the active workspace still enables
  // it) → the workspace default → leave i18n as-is. This runs at the session
  // level so every app inherits it, makes the server value authoritative over
  // the `i18nextLng` localStorage cache, and re-resolves on workspace switch and
  // whenever the profile language is changed (the settings Select is now the
  // only in-app switcher). Pre-login there is no `user`, so the auth-page
  // switcher and the detector cache still govern.
  useEffect(() => {
    if (!user) {
      return
    }

    const currentOrg =
      user.organizations?.find(
        (organization) => Number(organization.id) === activeWorkspaceId
      ) ?? user.organizations?.[0]

    const enabled = currentOrg?.enabled_languages ?? []
    const userLang = user.user.language

    const resolved =
      userLang && enabled.includes(userLang)
        ? userLang
        : currentOrg?.default_language

    const isSupported =
      resolved != null && allLangs.some((lang) => lang.value === resolved)

    if (isSupported && i18n.language.split('-')[0] !== resolved) {
      void i18n.changeLanguage(resolved)
    }
  }, [user, activeWorkspaceId, i18n])

  const hasSession = !!cookieData?.ok || hasAccessToken
  const isLoading = isCookieLoading || isUserLoading
  // The intentionally-remembered workspace (see the persistence effect). Read
  // straight from storage so the landing decision never confuses it with the
  // in-memory fallback that `activeWorkspaceId` carries.
  const storedWorkspaceId = isBrowser ? getStoredWorkspaceId() : undefined
  const contextValue = useMemo(
    () => ({
      user,
      isLoading,
      activeWorkspaceId,
      storedWorkspaceId,
      setActiveWorkspaceId,
      updateUserData,
      removeUserData,
      refreshSession
    }),
    [
      user,
      isLoading,
      activeWorkspaceId,
      storedWorkspaceId,
      setActiveWorkspaceId,
      updateUserData,
      removeUserData,
      refreshSession
    ]
  )

  if (shouldRedirectToLogin) {
    const redirectUrl = `${pathname}${search}`

    return (
      <Navigate
        to={`${paths.auth.login}?redirect=${encodeURIComponent(redirectUrl)}`}
        replace
      />
    )
  }

  if (!isOnPublicAuthPage && (isCookieLoading || (hasSession && isLoading))) {
    return <AppLoadingScreen />
  }

  return (
    <UserSessionContext.Provider value={contextValue}>
      {children}
    </UserSessionContext.Provider>
  )
}
